import { useCallback, FC } from 'react';
import { useTranslations } from '@altermaind/core/i18n';
import { Box, Typography, Button, Svg, useThemeValue } from '@altermaind/web/ui';
import { convertSizeToNumber } from '@/_shared/helpers/convertSizeToNumber';
import { useDialog } from '@/_shared/hooks';
import { WIZARD_STEPS } from '@/configuration/constants/wizard';
import {
  getEmployeesImportCall,
  getEmployeesImportsByJobIdCall,
  getEmployeesImportsStatsCall,
  postEmployeesImportsTemplateCall,
} from '@/people/state';
import { DialogConfirmNavigateBackPayload } from './types';

export const DialogConfirmNavigateBack: FC = () => {
  const t = useTranslations('configuration.hr.verifyData.dialogConfirmNavigateBack');
  const gapL = convertSizeToNumber(`${useThemeValue('sizes', 'l')}`);
  const { closeDialog, Dialog, isOpen, payload } = useDialog<DialogConfirmNavigateBackPayload>(
    'uploadCompanyDataConfirmNavigateBack',
  );
  const { reset: resetPostEmployeesImportsTemplate } = postEmployeesImportsTemplateCall.hook();
  const { reset: resetGetEmployeesImportsByJobId } = getEmployeesImportsByJobIdCall.hook();
  const { reset: resetGetEmployeesImportsStats } = getEmployeesImportsStatsCall.hook();
  const { reset: resetGetEmployeesImport } = getEmployeesImportCall.hook();

  const buttonClickHandler = useCallback(() => {
    resetPostEmployeesImportsTemplate();
    resetGetEmployeesImportsByJobId();
    resetGetEmployeesImportsStats();
    resetGetEmployeesImport();
    payload?.abort?.();
    payload?.navigate?.(WIZARD_STEPS.SET_COMPANY_DATA);
    closeDialog();
  }, [
    closeDialog,
    payload,
    resetGetEmployeesImport,
    resetGetEmployeesImportsByJobId,
    resetGetEmployeesImportsStats,
    resetPostEmployeesImportsTemplate,
  ]);

  return (
    <Dialog maxWidth='700px' open={isOpen} title={undefined} onClose={closeDialog} closeButton>
      <Box display='flex' mx='l' flexDirection='column' px='s'>
        <Typography variant='h3' color='primary/text' textAlign='center'>
          {t('title')}
        </Typography>
      </Box>
      <Box display='flex' mt='l' flexDirection='column' px='s'>
        <Typography variant='p2/light' color='primary/text' textAlign='center'>
          {t('description')}
        </Typography>
      </Box>

      <Box display='flex' mt='m' flexDirection='column' px='s'>
        <Typography variant='p2/bold' color='primary/text' textAlign='center'>
          {t('explicitQuestion')}
        </Typography>
      </Box>

      <Box
        display='flex'
        mx='l'
        flexDirection='row'
        px='s'
        style={{ gap: gapL }}
        justifyContent='center'
      >
        <Button
          variant='link/primary'
          onClick={closeDialog}
          spinnerColor='white'
          mt='l'
          style={{ textDecoration: 'none' }}
        >
          <Svg name='close' fill='primary/text' size='s' mr='xs' />
          {t('cancel')}
        </Button>

        <Button variant='primary' spinnerColor='white' onClick={buttonClickHandler} mt='l'>
          {t('confirm')}
        </Button>
      </Box>
    </Dialog>
  );
};
